<template>
  <div
    :class="[
      'app-notification',
      `app-notification--${variant}`,
      { 'app-notification--compact': compact },
    ]"
  >
    <Confetti v-if="confetti" class="app-notification__confetti" />

    <AppNotificationIcon
      v-if="!compact"
      class="app-notification__icon"
      :variant
    />

    <div class="app-notification__content">
      <h2 v-if="$slots.title" class="app-notification__title">
        <slot name="title"></slot>
      </h2>
      <p class="app-notification__message">
        <slot name="message"></slot>
      </p>
    </div>

    <div class="app-notification__actions">
      <slot name="action">
        <AppButton
          v-for="(action, index) in actions"
          :key="index"
          :dense="true"
          :outlined="compact || index !== 0"
          class="app-notification__action"
          @click="action.onClick()"
        >
          {{ action.label }}
        </AppButton>
      </slot>
    </div>

    <button
      v-if="!compact"
      class="app-notification__close"
      @click="$emit('close')"
    >
      <span class="app-notification__close-icon iconfont iclose-thin"></span>
    </button>
  </div>
</template>

<script setup lang="ts">
import type {
  NotificationAction,
  NotificationVariant,
} from '@/services/notification.app'
import AppButton from '@/app/components/button/AppButton.vue'
import Confetti from '@/components/ui/Confetti.vue'
import AppNotificationIcon from './AppNotificationIcon.vue'

/**
 * This is component is bare component for displaying notifications.
 * It defines specific styles for the different variants.
 *
 * @param variant (NotificationVariant) - Visual style variant:
 * 'unique' | 'success' | 'error' | 'warning' | 'info'. Default: 'unique'
 * @param actions (NotificationAction[]) - Array of action buttons with
 * `{ label: string, onClick: () => void }`. Default: []
 *   Note - if no actions are passed, the close button is displayed
 *        you can use the action slot to override all default action behavior
 * @param compact (boolean) - Compact confirmation style: the message with a
 * trailing outlined action, and no icon or close button. Default: false
 * @event close - Emitted when the close button is clicked
 */
const {
  variant = 'unique',
  actions = [],
  confetti = false,
  compact = false,
} = defineProps<{
  variant?: NotificationVariant
  actions?: NotificationAction[]
  confetti?: boolean
  compact?: boolean
}>()

defineEmits(['close'])
</script>

<style lang="scss" scoped>
.app-notification {
  position: relative;
  display: flex;
  gap: 8px 12px;
  align-items: center;
  width: 100%;
  padding: 20px;
  border-radius: 12px;
  box-shadow: $shadow;
  background: var(--notification-background);
  border: 1px solid var(--notification-border);

  @media (max-width: $w-mobile-s) {
    padding: 12px;
    gap: 4px 8px;
  }

  &__icon {
    width: 28px;
    height: 28px;

    @media (max-width: $w-mobile-s) {
      width: 20px;
      height: 20px;
    }
  }

  &:has(.app-notification__title) &__icon {
    margin-bottom: auto;
  }

  &__content {
    flex: 1;
    height: fit-content;
  }

  &__close {
    display: grid;
    place-items: center;
    height: fit-content;
    padding: 4px;
    margin: -4px;
    border-radius: 6px;
    color: $c-black-3;

    @include hover {
      color: $c-black-2;
      background: rgba($c-black-2, 0.05);
    }

    .night-mode & {
      color: $c-black-4;

      @include hover {
        color: $c-white;
      }
    }

    .iconfont {
      display: block;
      width: 24px;
      height: 24px;
      font-size: 24px;

      @media (max-width: $w-mobile-s) {
        width: 18px;
        height: 18px;
        font-size: 18px;
      }
    }
  }

  &__title {
    font-size: 18px;
    line-height: 23px;
    margin-top: 4px;
    font-weight: 500;

    @media (max-width: $w-mobile-s) {
      font-size: 16px;
      line-height: 20px;
      margin: 0;
    }
  }

  &__message {
    margin-top: 4px;
    font-size: 16px;
    line-height: 24px;
    color: $c-black-3;

    .night-mode & {
      color: $c-black-4;
    }

    @media (max-width: $w-mobile-s) {
      font-size: 14px;
      line-height: 18px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 8px;
    flex-shrink: 0;

    @media (max-width: $w-mobile-s) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__action {
    padding-inline: 0;

    @media (max-width: $w-mobile-s) {
      height: 28px;
      font-size: 13px;
    }
  }

  &--unique {
    --notification-background: #{$c-bg};
    --notification-border: #{$c-black-13};

    .night-mode & {
      --notification-background: #{$c-dark-2};
      --notification-border: #{rgba($c-black-3, 0.5)};
    }
  }

  &--info {
    --notification-background: #{$c-blue-6};
    --notification-border: #{rgba($c-blue-4, 0.3)};

    .night-mode & {
      --notification-background: #{$c-blue-7};
    }
  }

  &--success {
    --notification-background: #{$c-green-3};
    --notification-border: #{rgba($c-green-2, 0.3)};

    .night-mode & {
      --notification-background: #{$c-green-4};
      --notification-border: #{rgba($c-green-2, 0.5)};
    }
  }

  &--warning {
    --notification-background: #{$c-yellow-2};
    --notification-border: #{rgba($c-yellow, 0.3)};

    .night-mode & {
      --notification-background: #{$c-yellow-11};
    }
  }

  &--error {
    --notification-background: #{$c-red-2};
    --notification-border: #{rgba($c-red-6, 0.3)};

    .night-mode & {
      --notification-background: #{$c-red-7};
    }
  }

  // Confirmation toast layout: the message with a trailing outlined action.
  &--compact {
    padding: 12px 12px 12px 24px;
    gap: 16px;

    // Shift the text down 1px so that it looks vertically centered. This is
    // necessary because the font letters have more space below than above.
    .app-notification__message {
      margin-top: 0;
      padding-top: 2px;
      color: $c-black-2;

      .night-mode & {
        color: $c-dark-4;
      }
    }

    .app-notification__action {
      // The dense button pads both itself and its label by 8px/6px, which
      // pushes the label below centre. Keep only the label's padding: its
      // 1px downward shift matches the message's.
      height: auto;
      padding: 0 19px;
      border-color: $c-black-3;
      background: transparent;
      color: $c-black-2;

      @include hover {
        border-color: $c-black-2;
        background: rgba($c-black-2, 0.05);
      }

      .night-mode & {
        border-color: $c-dark-6;
        color: $c-dark-4;

        @include hover {
          border-color: $c-white;
          background: rgba($c-white, 0.05);
        }
      }
    }

    @media (max-width: $w-mobile-s) {
      padding: 8px 8px 8px 16px;
    }
  }
}
</style>
